import { z } from "zod";
import { frontDeskAccessSchema } from "../front-desk-access.ts";
import {
  attendanceModeSchema,
  bookingForSchema,
  classFillSchema,
  businessRoleSchema,
  teamRoleSchema,
  devicePlatformSchema,
  membershipStatusSchema,
  participantKindSchema,
  phoneSignInChannelSchema,
  signInChannelSchema,
  signInMethodKindSchema,
  placementStatusSchema,
  sportSchema,
  teamInvitationStatusSchema,
} from "../enums.ts";
import { appAccentSchema, businessLabelsSchema, businessLogoSchema } from "./business.ts";
import { assistantPackageSchema } from "./assistant.ts";
import { isoDateSchema, isoDateTimeSchema, timestampsSchema, uuidSchema } from "./common.ts";
import { businessBillingSummarySchema } from "./subscription.ts";

export const userSchema = z
  .object({
    id: uuidSchema,
    clerkUserId: z.string(),
    /** Null for an account that signs in by email only. */
    phoneE164: z.string().nullable(),
    fullName: z.string().nullable(),
    email: z.string().email().nullable(),
    /** Set once a code sent to the number came back. Only a verified number signs in. */
    phoneVerifiedAt: isoDateTimeSchema.nullable(),
    /** Null for an address stored before sign-in by email existed. Only a verified address signs in. */
    emailVerifiedAt: isoDateTimeSchema.nullable(),
    locale: z.string(),
    timezone: z.string(),
    /**
     * Cards or a list, for taking attendance. Null until the person picks one. Optional so clients
     * and fixtures written before this field keep parsing.
     */
    attendanceMode: attendanceModeSchema.nullable().optional(),
  })
  .merge(timestampsSchema);
export type User = z.infer<typeof userSchema>;

export const notificationPreferencesSchema = z.object({
  whatsappOptIn: z.boolean(),
  pushEnabled: z.boolean(),
});
export type NotificationPreferences = z.infer<typeof notificationPreferencesSchema>;

/** `GET /v1/me` */
export const meResponseSchema = z.object({
  user: userSchema,
  preferences: notificationPreferencesSchema,
  /** The user's own participant row, created automatically on first sign in. */
  selfParticipantId: uuidSchema,
  /**
   * False while Teamna is early access only and this account is not on the allow list
   * (`POST /v1/owner/businesses` then answers SIGN_UP_CLOSED). Optional so an older API still parses.
   */
  canCreateBusiness: z.boolean().optional(),
});
export type MeResponse = z.infer<typeof meResponseSchema>;

/**
 * `PATCH /v1/me`. There is no `email` here on purpose: an email address signs in, so it only
 * changes through `/v1/me/sign-in-methods`, with a code. An old client that still sends one has it
 * dropped by the schema.
 */
export const updateMeRequestSchema = z
  .object({
    fullName: z.string().min(1).max(120),
    locale: z.string().min(2).max(10),
    timezone: z.string().min(3).max(64),
    whatsappOptIn: z.boolean(),
    pushEnabled: z.boolean(),
    attendanceMode: attendanceModeSchema,
  })
  .partial();
export type UpdateMeRequest = z.infer<typeof updateMeRequestSchema>;

/** `POST /v1/me/devices` */
/**
 * An Expo push token: `ExponentPushToken[...]` (or the newer `ExpoPushToken[...]`), at most 200
 * characters. Bounded because the column has a unique index, which refuses very long values with a
 * server error (push sign-out review F11.1).
 */
export const expoPushTokenSchema = z
  .string()
  .min(10)
  .max(200)
  .regex(/^Expo(nent)?PushToken\[[^\]\s]+\]$/, "not an Expo push token");

export const registerDeviceRequestSchema = z.object({
  expoPushToken: expoPushTokenSchema,
  platform: devicePlatformSchema,
});
export type RegisterDeviceRequest = z.infer<typeof registerDeviceRequestSchema>;

/** Sign-out on a phone: take this push token off the account, so the next person never gets its pushes. */
export const removeDeviceRequestSchema = z.object({ expoPushToken: expoPushTokenSchema });
export type RemoveDeviceRequest = z.infer<typeof removeDeviceRequestSchema>;

export const deviceSchema = z
  .object({
    id: uuidSchema,
    userId: uuidSchema,
    expoPushToken: z.string(),
    platform: devicePlatformSchema,
    lastSeenAt: isoDateTimeSchema,
  })
  .merge(timestampsSchema);
export type Device = z.infer<typeof deviceSchema>;

export const participantSchema = z
  .object({
    id: uuidSchema,
    userId: uuidSchema,
    businessId: uuidSchema.nullable(),
    kind: participantKindSchema,
    displayName: z.string(),
    dateOfBirth: isoDateSchema.nullable(),
    birthYear: z.number().int().nullable(),
    notes: z.string().nullable(),
    groupId: uuidSchema.nullable(),
    usualWeekdays: z.array(z.number().int().min(0).max(6)),
    placementStatus: placementStatusSchema,
    confirmedAt: isoDateTimeSchema.nullable(),
    /** The profile photo at the participant's business: a signed address that works for some hours. */
    photoUrl: z.string().nullable(),
    /**
     * The family said "no profile photo" (migration 0029). The API always sends it; optional only
     * so fixtures written before it keep parsing. Missing reads as false.
     */
    photosRefused: z.boolean().optional(),
    /**
     * Only on the account holder's own row (GET /me/participants): the businesses that have the
     * person on their lists by a link of their own, whatever business holds the row (D36). The member
     * screens offer the row there (`peopleAt`). Optional for older API builds and the other answers.
     */
    listedAt: z.array(uuidSchema).optional(),
  })
  .merge(timestampsSchema);
export type Participant = z.infer<typeof participantSchema>;

export const createParticipantRequestSchema = z.object({
  displayName: z.string().min(1).max(120),
  /** An academy places a child by age, so this is the one detail past the name that it needs. */
  dateOfBirth: isoDateSchema.nullable().optional(),
  birthYear: z.number().int().min(1900).max(2100).nullable().optional(),
  notes: z.string().max(1000).nullable().optional(),
  /** The business this participant is being added to. Required for group and usual days to mean anything. */
  businessId: uuidSchema.optional(),
  /** The group the member picked, or null with `unsure: true` for "ask staff". */
  groupId: uuidSchema.nullable().optional(),
  unsure: z.boolean().optional(),
  usualWeekdays: z.array(z.number().int().min(0).max(6)).optional(),
});
export type CreateParticipantRequest = z.infer<typeof createParticipantRequestSchema>;

export const updateParticipantRequestSchema = createParticipantRequestSchema.partial();
export type UpdateParticipantRequest = z.infer<typeof updateParticipantRequestSchema>;

/** One row of `GET /v1/me/businesses`: how this user relates to a business, from both directions. */
export const myBusinessSchema = z.object({
  businessId: uuidSchema,
  name: z.string(),
  slug: z.string(),
  timezone: z.string(),
  currency: z.string(),
  /** Set when the user operates the business. Null for a plain member. */
  staffRole: businessRoleSchema.nullable(),
  /** Set when the user is a member of the business. Null for a pure operator. */
  membershipStatus: membershipStatusSchema.nullable(),
  sport: sportSchema,
  /**
   * The ten words this business uses for its people and its groups, the same shape
   * `GET /v1/owner/businesses/:id` returns. Here so every role that can see a business (owner,
   * admin, staff, member) can read its words. The API always sends it, and falls back to
   * `defaultLabels(sport)` when a business has no labels row.
   *
   * Optional in the schema only so that clients and test fixtures written before this field keep
   * parsing. A reader that finds it missing uses `defaultLabels(sport)`, the same fallback.
   */
  labels: businessLabelsSchema.optional(),
  /**
   * The business shape (migration 0069): who books and how classes fill. Every role reads it, so
   * each screen can follow the shape. Optional in the schema for older clients and fixtures; read
   * it with `businessShape()`, which falls back to the sport's start value.
   */
  bookingFor: bookingForSchema.optional(),
  classFill: classFillSchema.optional(),
  /**
   * The live logo, or null when the business shows its initials. Optional in the schema for the
   * same reason as `labels`: older clients and fixtures. Missing reads as null.
   */
  logo: businessLogoSchema.nullable().optional(),
  /**
   * The colour of the Teamna app for this business, or null for Teamna blue (slice 2.2). Every role
   * reads it, so the phone draws the business's colour for the team and the members alike. Optional
   * for older clients and fixtures; missing reads as null.
   */
  appAccent: appAccentSchema.optional(),
  /**
   * The Teamna plan of this business (subscription billing), for the owner and an admin only. Null
   * for staff and members, for a demo business, and when billing is off on the server. Optional in
   * the schema for older clients and fixtures; missing reads as null (full access, no banner).
   */
  billing: businessBillingSummarySchema.nullable().optional(),
  /**
   * The signed-in person's own profile photo at this business (migration 0029), when they are on
   * its team. A signed URL that works for some hours. Optional for older clients; missing reads as null.
   */
  myPhotoUrl: z.string().nullable().optional(),
  /**
   * True when a member can pay this business by card now (Stripe Connect: the business's account is
   * verified and takes payments; always true with the stub provider). When false, member screens
   * show the cash and bank transfer path instead of a card checkout that the API would refuse.
   * Optional for older clients; missing reads as true (the old behaviour).
   */
  cardPayments: z.boolean().optional(),
  /**
   * True while card payments run on the stub provider (no Stripe on this server): the checkout is a
   * test page and no money moves, and member screens say so. Optional for older clients.
   */
  cardPaymentsTest: z.boolean().optional(),
  /**
   * True while Teamna has card payments off for all businesses (Control Center switch). Owner
   * screens then show "Coming soon" for every card payment part; `cardPayments` is false too.
   * Optional for older clients; missing reads as false.
   */
  cardPaymentsComingSoon: z.boolean().optional(),
  /**
   * True when the signed-in person may use AI Assistant here: the owner or an admin, and the business
   * bought the package or Teamna gave it (`assistantPackage`). Optional for older clients; missing reads as false.
   */
  aiAssistant: z.boolean().optional(),
  /**
   * The AI Assistant package of this business, for the owner and an admin only (null for everybody
   * else). Desk and the app show the locked screen with "Add AI Assistant" from it. Optional for older clients.
   */
  assistantPackage: assistantPackageSchema.nullable().optional(),
  /**
   * What the Front desk role may see and do here (`settings.frontDesk`, every item filled in), for a
   * person on the team; null for a member. Desk and the phone read it through `deskMay` and
   * `frontDeskMay`. Optional for older clients and fixtures; missing reads as the start values.
   */
  frontDesk: frontDeskAccessSchema.nullable().optional(),
  /**
   * True when Desk session replay may show the text of the screens (not the inputs, not the images)
   * for this person here: the business is a Teamna pilot (`businesses.pilot_since`) and the person
   * is on its team. Members always stay fully masked. Optional for older clients; missing reads as false.
   */
  pilotReplay: z.boolean().optional(),
});
export type MyBusiness = z.infer<typeof myBusinessSchema>;

export const myBusinessesResponseSchema = z.object({ items: z.array(myBusinessSchema) });
export type MyBusinessesResponse = z.infer<typeof myBusinessesResponseSchema>;

/** `POST /v1/join/:code` */
export const joinResponseSchema = z.object({
  businessId: uuidSchema,
  businessName: z.string(),
  /** "member" creates a membership, "staff" creates a business_members row. */
  joinedAs: z.enum(["member", "staff"]),
  membershipStatus: membershipStatusSchema.nullable(),
  alreadyJoined: z.boolean(),
  /** True when the phone number was on the owner's imported list, so no approval was needed. */
  recognised: z.boolean(),
  ownerName: z.string().nullable(),
});
export type JoinResponse = z.infer<typeof joinResponseSchema>;

/**
 * Why a signed-in person can join a business without a code (`services/join-offers.ts`):
 * `added` the business added their family, `invited` the business invited their number, `left` the
 * membership ended and the family is still on the business's lists.
 */
export const joinOfferReasons = ["added", "invited", "left"] as const;
export type JoinOfferReason = (typeof joinOfferReasons)[number];

/** `GET /v1/me/join-offers` */
export const joinOfferSchema = z.object({
  businessId: uuidSchema,
  businessName: z.string(),
  logo: businessLogoSchema.nullable(),
  reason: z.enum(joinOfferReasons),
});
export type JoinOffer = z.infer<typeof joinOfferSchema>;
export const joinOffersResponseSchema = z.object({ offers: z.array(joinOfferSchema) });
export type JoinOffersResponse = z.infer<typeof joinOffersResponseSchema>;

// ---------------------------------------------------------------------------
// Sign-in (auth spec V2): one code flow for a mobile number or an email address
// ---------------------------------------------------------------------------

/** `GET /v1/auth/options`: what the sign-in screen offers before anything is typed. */
export const signInOptionsResponseSchema = z.object({
  /** The chip selected first when the identifier is a number. */
  defaultPhoneChannel: phoneSignInChannelSchema,
  /** False when email sign-in is switched off. The field then asks for a number only. */
  emailEnabled: z.boolean(),
  /**
   * The phone channels that really carry a code now, the default first. One button each on the
   * sign-in screen. Both when nothing is sent (fixed local code), so testers see the real layout.
   */
  phoneChannels: z.array(phoneSignInChannelSchema).default(["whatsapp", "sms"]),
  /** ISO 3166 countries an SMS may go to. The SMS button shows only for these. */
  smsCountries: z.array(z.string().length(2)).default(["AE"]),
  /** The visitor's country when the hosting platform reports it, for the country picker. */
  country: z.string().length(2).nullable().default(null),
  /**
   * "Continue with WhatsApp" without a template: the person sends us a prefilled message and is
   * signed in when it arrives (`/auth/whatsapp-link/*`). Independent of `phoneChannels`.
   */
  whatsappLink: z.boolean().default(false),
});
export type SignInOptionsResponse = z.infer<typeof signInOptionsResponseSchema>;

/** `GET /v1/auth/invite?code=`: the business a join link names, shown on the sign-in screen. */
export const inviteLookupResponseSchema = z.object({
  businessName: z.string(),
  code: z.string(),
  /** The business's live logo, so the sign-in screen can show it. Null or missing: no logo. */
  logo: businessLogoSchema.nullable().optional(),
});
export type InviteLookupResponse = z.infer<typeof inviteLookupResponseSchema>;

/** `GET /v1/auth/team-invitation?token=`: what a personal team invitation link shows. Public. */
export const teamInvitationLookupResponseSchema = z.object({
  /** For the colour of the business mark when the business has no logo. */
  businessId: uuidSchema,
  businessName: z.string(),
  logo: businessLogoSchema.nullable().optional(),
  role: teamRoleSchema,
  /** The business's word for a staff member ("coach", "trainer"), for the page to say the role. */
  staffWord: z.string(),
  /** The first name of the person who sent it. Null when the account has no name. */
  inviterName: z.string().nullable(),
  /** The number the invitation is for, masked. The person signs in with this number. */
  phoneMasked: z.string(),
  /** `pending`: open it. `accepted`: already in. `revoked` or `replaced`: the link no longer works. */
  status: teamInvitationStatusSchema,
});
export type TeamInvitationLookupResponse = z.infer<typeof teamInvitationLookupResponseSchema>;

/** `POST /v1/auth/start` and `POST /v1/me/sign-in-methods/start` */
export const signInStartRequestSchema = z.object({
  /** A mobile number (UAE numbers may be typed locally) or an email address. */
  identifier: z.string().min(3).max(254),
  /** For a number only. Omitted means the configured default. Ignored for an email. */
  channel: phoneSignInChannelSchema.optional(),
});
export type SignInStartRequest = z.infer<typeof signInStartRequestSchema>;

/** `POST /v1/auth/start` */
export const signInStartResponseSchema = z.object({
  /** The identifier as the server understood it (E.164 or a lowercase address), to show back. */
  identifier: z.string(),
  kind: signInMethodKindSchema,
  /** The channel the code actually went on, which can differ from the one asked for. */
  channel: signInChannelSchema,
  expiresInMinutes: z.number().int(),
  /** True when nothing was sent and the code is the fixed one, 123456 (a local API with no channel; never staging or production). */
  simulated: z.boolean(),
});
export type SignInStartResponse = z.infer<typeof signInStartResponseSchema>;

/** `POST /v1/auth/verify` and `POST /v1/me/sign-in-methods/verify` */
export const signInVerifyRequestSchema = z.object({
  identifier: z.string().min(3).max(254),
  code: z.string().min(4).max(8),
});
export type SignInVerifyRequest = z.infer<typeof signInVerifyRequestSchema>;

/** The account as the sign-in screens need it. */
export const signInUserSchema = z.object({
  id: uuidSchema,
  fullName: z.string().nullable(),
  phoneE164: z.string().nullable(),
  email: z.string().nullable(),
  phoneVerifiedAt: isoDateTimeSchema.nullable(),
  emailVerifiedAt: isoDateTimeSchema.nullable(),
});
export type SignInUser = z.infer<typeof signInUserSchema>;

/** `POST /v1/auth/verify` */
/** `POST /v1/auth/demo-link`: the token from a Control Center magic link (the URL fragment). */
export const demoLinkSignInRequestSchema = z.object({
  token: z.string().min(20).max(200),
});
export type DemoLinkSignInRequest = z.infer<typeof demoLinkSignInRequestSchema>;

/** `POST /v1/auth/email-link/start`: email a sign-in link (Desk on the web). */
export const emailLinkStartRequestSchema = z.object({
  email: z.string().min(3).max(254),
  /**
   * An invite code waiting for sign-in (`/join/:code`). The link carries it, so the tab the link
   * opens joins the business too.
   */
  joinCode: z
    .string()
    .regex(/^[A-Za-z0-9]{4,16}$/)
    .optional(),
});
export type EmailLinkStartRequest = z.infer<typeof emailLinkStartRequestSchema>;

/** `POST /v1/auth/email-link/start` */
export const emailLinkStartResponseSchema = z.object({
  /** The address as the server understood it (lowercase), to show back. */
  email: z.string(),
  expiresInMinutes: z.number().int(),
  /** True when no email was sent because no email provider is configured (local runs only). */
  simulated: z.boolean(),
  /** Only when `simulated`: the link that the email would carry, so a local run can sign in. */
  link: z.string().url().optional(),
});
export type EmailLinkStartResponse = z.infer<typeof emailLinkStartResponseSchema>;

/** `POST /v1/auth/email-link`: the token from an emailed sign-in link (the URL fragment). */
export const emailLinkSignInRequestSchema = z.object({
  token: z.string().min(20).max(200),
});
export type EmailLinkSignInRequest = z.infer<typeof emailLinkSignInRequestSchema>;

export const signInVerifyResponseSchema = z.object({
  /** Send as `Authorization: Bearer <token>` on every later request. */
  token: z.string(),
  user: signInUserSchema,
  businesses: z.array(z.object({ id: uuidSchema, name: z.string(), role: z.string() })),
});
export type SignInVerifyResponse = z.infer<typeof signInVerifyResponseSchema>;

/** `POST /v1/auth/whatsapp-link/start`: a code to send us on WhatsApp, and how to send it. */
export const whatsappSignInStartResponseSchema = z.object({
  requestId: uuidSchema,
  /** Only this screen holds it. Polling needs it, so nobody else can collect the sign-in. */
  pollToken: z.string().min(20),
  code: z.string(),
  /** The text the wa.me link fills in. */
  message: z.string(),
  /** https://wa.me/<our number>?text=<message>. Opens WhatsApp with the message ready to send. */
  link: z.string().url(),
  expiresInMinutes: z.number().int(),
});
export type WhatsAppSignInStartResponse = z.infer<typeof whatsappSignInStartResponseSchema>;

/** `POST /v1/auth/whatsapp-link/poll` */
export const whatsappSignInPollRequestSchema = z.object({
  requestId: uuidSchema,
  pollToken: z.string().min(20).max(200),
});
export type WhatsAppSignInPollRequest = z.infer<typeof whatsappSignInPollRequestSchema>;

/** Pending until the message arrives; then one `signed_in` with the token, and never again. */
export const whatsappSignInPollResponseSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("pending") }),
  z.object({ status: z.literal("expired") }),
  signInVerifyResponseSchema.extend({ status: z.literal("signed_in") }),
]);
export type WhatsAppSignInPollResponse = z.infer<typeof whatsappSignInPollResponseSchema>;

/** `POST /v1/me/sign-in-methods/start` */
export const addSignInMethodStartResponseSchema = z.union([
  signInStartResponseSchema.extend({ alreadyVerified: z.literal(false).optional() }),
  z.object({ alreadyVerified: z.literal(true) }),
]);
export type AddSignInMethodStartResponse = z.infer<typeof addSignInMethodStartResponseSchema>;

/** `DELETE /v1/me/sign-in-methods/{kind}` */
export const signInMethodKindParamSchema = z.object({ kind: signInMethodKindSchema });

/**
 * `GET /v1/me/deletion`: what deleting the account would do, and what stops it now (mobile V2,
 * slice 0.2). Changes nothing.
 */
export const accountDeletionBlockerSchema = z.object({
  /** owns_business: they own a business. upcoming_bookings: a booking still to come. family_shared: another adult uses their family. */
  kind: z.enum(["owns_business", "upcoming_bookings", "family_shared"]),
  /** The businesses it is about (owns_business), else empty. */
  businessNames: z.array(z.string()),
  count: z.number().int().min(0),
});
export const accountDeletionSummarySchema = z.object({
  canDelete: z.boolean(),
  blockers: z.array(accountDeletionBlockerSchema),
  /** Every business the person belongs to: their records there stay, under "Deleted account". */
  businessNames: z.array(z.string()),
  /** Children on the account (not archived): their names and details go. */
  childCount: z.number().int().min(0),
});
export type AccountDeletionSummary = z.infer<typeof accountDeletionSummarySchema>;

/** `DELETE /v1/me`. The app asks the person to confirm first; the body says they did. */
export const deleteAccountRequestSchema = z.object({ confirm: z.literal(true) });
